import countriesData from '../data/countries-states-cities-data';

export const EMPTY_FOREIGN_GUEST_DETAILS = {
  nationality: '',
  passportNumber: '',
  passportType: 'ordinary',
  passportIssueDate: null,
  passportExpiryDate: null,
  passportIssuePlace: '',
  passportIssuingCountry: '',
  visaNumber: '',
  visaType: 'tourist',
  visaIssueDate: null,
  visaExpiryDate: null,
  visaIssuePlace: '',
  visaIssuingAuthority: '',
  arrivalFrom: '',
  arrivalMode: 'air',
  arrivalTransportNumber: '',
  arrivedInCountryOn: null,
};

export const PASSPORT_TYPE_OPTIONS = [
  { value: 'ordinary', label: 'Ordinary' },
  { value: 'official', label: 'Official' },
  { value: 'diplomatic', label: 'Diplomatic' },
  { value: 'service', label: 'Service' },
  { value: 'other', label: 'Other' },
];

export const VISA_TYPE_OPTIONS = [
  { value: 'tourist', label: 'Tourist' },
  { value: 'business', label: 'Business' },
  { value: 'employment', label: 'Employment' },
  { value: 'student', label: 'Student' },
  { value: 'medical', label: 'Medical' },
  { value: 'transit', label: 'Transit' },
  { value: 'oci', label: 'OCI / PIO' },
  { value: 'other', label: 'Other' },
];

export const ARRIVAL_MODE_OPTIONS = [
  { value: 'air', label: 'Air' },
  { value: 'rail', label: 'Rail' },
  { value: 'road', label: 'Road' },
  { value: 'sea', label: 'Sea' },
];

export function resolveCountryCode(countryValue) {
  if (!countryValue) return '';
  const raw = String(countryValue).trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const match = (countriesData.countries || []).find(
    (c) =>
      c.code === raw ||
      c.name === raw ||
      String(c.name || '').toLowerCase() === raw.toLowerCase()
  );
  return match?.code || '';
}

export function resolveCountryName(countryValue) {
  const code = resolveCountryCode(countryValue);
  if (!code) return String(countryValue || '').trim();
  const match = (countriesData.countries || []).find((c) => c.code === code);
  return match?.name || code;
}

/** Guest is foreign when their country differs from the hotel's configured country. */
export function isForeignGuestCountry(guestCountry, hotelCountry) {
  const guestCode = resolveCountryCode(guestCountry);
  const hotelCode = resolveCountryCode(hotelCountry);
  if (!guestCode || !hotelCode) return false;
  return guestCode !== hotelCode;
}

/** True when a stay/room guest record is from outside the hotel country. */
export function isForeignGuestRecord(guest, hotelCountry) {
  if (!guest) return false;
  return isForeignGuestCountry(guest.country || guest.nationality, hotelCountry);
}

/** Chip styles matching the Child identity chip, in a distinct foreign-guest color. */
export function getForeignGuestChipSx(isDark, { height = 18, fontSize = '0.6rem' } = {}) {
  return {
    height,
    fontSize,
    fontWeight: 700,
    flexShrink: 0,
    bgcolor: isDark ? 'rgba(156, 39, 176, 0.22)' : 'rgba(123, 31, 162, 0.12)',
    color: isDark ? '#ce93d8' : '#6a1b9a',
    border: `1px solid ${isDark ? 'rgba(206, 147, 216, 0.45)' : 'rgba(106, 27, 154, 0.3)'}`,
    '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
  };
}

/**
 * Walk-in / stay-form country change: if the new country is not the hotel
 * country, open Foreign Guest Details and keep nationality aligned.
 */
export function applyForeignCountryChange(countryName, hotelCountry, currentDetails = EMPTY_FOREIGN_GUEST_DETAILS) {
  if (!hotelCountry || !isForeignGuestCountry(countryName, hotelCountry)) {
    return {
      isForeign: false,
      shouldOpenDrawer: false,
      details: currentDetails,
    };
  }
  const nextCode = resolveCountryCode(countryName);
  const prevNationality = resolveCountryCode(currentDetails.nationality);
  const prevIssuing = resolveCountryCode(currentDetails.passportIssuingCountry);
  return {
    isForeign: true,
    shouldOpenDrawer: true,
    details: {
      ...currentDetails,
      nationality: nextCode,
      passportIssuingCountry:
        !prevIssuing || prevIssuing === prevNationality ? nextCode : prevIssuing,
    },
  };
}

export function getForeignGuestDrawerCopy(hotelCountry) {
  const hotelCode = resolveCountryCode(hotelCountry) || 'IN';
  const hotelName = resolveCountryName(hotelCode) || 'this country';
  return {
    badge: 'Foreign guest',
    title: 'Foreign Guest Details',
    subtitle: `Travel documents for guests from outside ${hotelName}.`,
    arrivalDateLabel: `Arrival date in ${hotelName}`,
    saveLabel: 'Save details',
  };
}

export function hasForeignGuestDetailsValues(details = {}) {
  return Boolean(
    details.passportNumber ||
      details.visaNumber ||
      details.arrivalFrom ||
      details.arrivedInCountryOn ||
      details.nationality
  );
}

/**
 * Mandatory for Form C readiness (room number comes from booking context).
 */
export function validateForeignGuestDetails(details = {}) {
  const errors = {};
  if (!String(details.nationality || '').trim()) {
    errors.nationality = 'Nationality is required';
  }
  if (!String(details.passportNumber || '').trim()) {
    errors.passportNumber = 'Passport number is required';
  }
  if (!details.passportType) {
    errors.passportType = 'Passport type is required';
  }
  if (!details.passportIssueDate) {
    errors.passportIssueDate = 'Passport issue date is required';
  }
  if (!details.passportExpiryDate) {
    errors.passportExpiryDate = 'Passport expiry date is required';
  }
  if (!String(details.passportIssuePlace || '').trim()) {
    errors.passportIssuePlace = 'Passport issue place is required';
  }
  if (!String(details.passportIssuingCountry || '').trim()) {
    errors.passportIssuingCountry = 'Passport issuing country is required';
  }
  if (!String(details.visaNumber || '').trim()) {
    errors.visaNumber = 'Visa number is required';
  }
  if (!String(details.visaType || '').trim()) {
    errors.visaType = 'Visa type is required';
  }
  if (!details.visaIssueDate) {
    errors.visaIssueDate = 'Visa issue date is required';
  }
  if (!details.visaExpiryDate) {
    errors.visaExpiryDate = 'Visa expiry date is required';
  }
  if (!String(details.visaIssuePlace || '').trim()) {
    errors.visaIssuePlace = 'Visa issue place is required';
  }
  if (!String(details.arrivalFrom || '').trim()) {
    errors.arrivalFrom = 'Arrival from is required';
  }
  if (!details.arrivalMode) {
    errors.arrivalMode = 'Mode of arrival is required';
  }
  if (!details.arrivedInCountryOn) {
    errors.arrivedInCountryOn = 'Arrival date is required';
  }
  return {
    ok: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Map API guest → foreign-details form.
 * Nationality always follows the selected/current country (form country), never a
 * stale `guest.nationality` that can disagree after phone-search + country edit.
 */
export function mapGuestApiToForeignDetails(guest = {}, selectedCountry = '') {
  const nationality =
    resolveCountryCode(selectedCountry) ||
    resolveCountryCode(guest.country) ||
    resolveCountryCode(guest.nationality) ||
    '';
  const passportNumber =
    String(guest.passport_number || '').trim() ||
    (String(guest.document_type || '').toLowerCase() === 'passport'
      ? String(guest.id_number || '').trim()
      : '');
  const prevIssuing = resolveCountryCode(guest.passport_issuing_country);
  const prevNationality = resolveCountryCode(guest.nationality);
  // Keep a deliberate issuing-country override; otherwise stay aligned with nationality.
  const passportIssuingCountry =
    prevIssuing && prevIssuing !== prevNationality ? prevIssuing : nationality || prevIssuing || '';
  return {
    ...EMPTY_FOREIGN_GUEST_DETAILS,
    nationality,
    passportNumber,
    passportType: guest.passport_type || 'ordinary',
    passportIssueDate: guest.passport_issue_date || null,
    passportExpiryDate: guest.passport_expiry_date || null,
    passportIssuePlace: guest.passport_issue_place || '',
    passportIssuingCountry,
    visaNumber: guest.visa_number || '',
    visaType: guest.visa_type || 'tourist',
    visaIssueDate: guest.visa_issue_date || null,
    visaExpiryDate: guest.visa_expiry_date || null,
    visaIssuePlace: guest.visa_issue_place || '',
    visaIssuingAuthority: guest.visa_issuing_authority || '',
    arrivalFrom: guest.arrival_from || '',
    arrivalMode: guest.arrival_mode || 'air',
    arrivalTransportNumber: guest.arrival_transport_number || '',
    arrivedInCountryOn: guest.arrived_in_country_on || null,
  };
}

export function foreignDetailsToGuestApiPayload(details = {}) {
  const formatDate = (value) => {
    if (!value) return null;
    if (typeof value?.format === 'function') return value.format('YYYY-MM-DD');
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    const text = String(value).trim();
    return text.length >= 10 ? text.slice(0, 10) : null;
  };
  const nationality = resolveCountryCode(details.nationality);
  const passportIssuingCountry = resolveCountryCode(details.passportIssuingCountry) || nationality;
  const passportNumber = String(details.passportNumber || '').trim();
  return {
    nationality,
    passport_number: passportNumber,
    passport_type: details.passportType || '',
    passport_issue_date: formatDate(details.passportIssueDate),
    passport_expiry_date: formatDate(details.passportExpiryDate),
    passport_issue_place: String(details.passportIssuePlace || '').trim(),
    passport_issuing_country: passportIssuingCountry,
    visa_number: String(details.visaNumber || '').trim(),
    visa_type: details.visaType || '',
    visa_issue_date: formatDate(details.visaIssueDate),
    visa_expiry_date: formatDate(details.visaExpiryDate),
    visa_issue_place: String(details.visaIssuePlace || '').trim(),
    visa_issuing_authority: String(details.visaIssuingAuthority || '').trim(),
    arrival_from: String(details.arrivalFrom || '').trim(),
    arrival_mode: details.arrivalMode || '',
    arrival_transport_number: String(details.arrivalTransportNumber || '').trim(),
    arrived_in_country_on: formatDate(details.arrivedInCountryOn),
    // Keep primary ID in sync for foreign guests (avoid duplicate capture).
    document_type: passportNumber ? 'passport' : undefined,
    id_number: passportNumber || undefined,
  };
}

/** Clear all foreign-travel fields on a guest (does not clear ID document images). */
export function clearForeignGuestApiPayload() {
  return {
    nationality: '',
    passport_number: '',
    passport_type: '',
    passport_issue_date: null,
    passport_expiry_date: null,
    passport_issue_place: '',
    passport_issuing_country: '',
    visa_number: '',
    visa_type: '',
    visa_issue_date: null,
    visa_expiry_date: null,
    visa_issue_place: '',
    visa_issuing_authority: '',
    arrival_from: '',
    arrival_mode: '',
    arrival_transport_number: '',
    arrived_in_country_on: null,
  };
}

export function optionLabel(options, value) {
  const match = (options || []).find((opt) => opt.value === value);
  return match?.label || (value ? String(value).replace(/_/g, ' ') : '—');
}
